/** @jsx isolateComponent */
import { css } from '@emotion/react';
import {
  Animation,
  Colors,
  Heading,
  HeadingLevel,
  Link,
  Spacer,
  Text,
  Typography,
} from '@zapier/design-system';
import { isolateComponent } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../hooks/useUniversalNavContext';
import type { PromoLinksListType } from '../../../constants';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { ArrowLinkIcon } from '../MegaMenuSubmenu';
import type { AnchorTarget } from '@zapier/design-system';

type Props = PromoLinksListType & {
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = {
  root: css({
    color: Colors.neutral800,
    ...Typography.smallPrint1Bold,
  }),
  listItem: css({
    marginBottom: 8,
    '&:last-of-type': {
      marginBottom: 0,
    },
  }),
  link: css({
    ...Typography.smallPrint1,
    color: Colors.neutral800,
    transition: `text-decoration 100ms ${Animation.transitionTimingFunction}`,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: Colors.neutral800,
    },
    '&:hover > span': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
      color: Colors.neutral800,
    },
  }),
};

/**
 * A list of promotional links in the MegaNav
 */
export const ListPromo = (props: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `ListPromo: ${text}`,
    });
    if (eventType === 'click' && props.onCloseMenu) props.onCloseMenu();
  };

  return (
    <section css={Styles.root}>
      {props.text && (
        <HeadingLevel>
          <Heading>
            <Text type="smallPrint1Bold">{props.text}</Text>
          </Heading>
          <Spacer height={20} />
        </HeadingLevel>
      )}
      <ul>
        {props.links.map((link) => (
          <li key={link.text} css={Styles.listItem}>
            <Link
              color={null}
              css={Styles.link}
              href={getFullyQualifiedURL(link.href) || undefined}
              onClick={() => handleEvent(link.text, 'click')}
              onMouseEnter={() => handleEvent(link.text, 'hover')}
              target={link.target as AnchorTarget}
            >
              {link.text}
              <ArrowLinkIcon />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};
